/**
 * Catálogo central de módulos y submódulos de GEMA ERP
 * Soporta control de acceso jerárquico y granular.
 */

/**
 * Correos autorizados para el rol exclusivo PERMISO_SOCIO.
 * Información global/consolidada y métricas financieras reservadas de la empresa.
 * Exclusivo para:
 * - Prem Gurbani (prem@imaginem.mx)
 * - Madelinne Granados (madelinne@imaginem.mx)
 */
export const SOCIO_EMAILS = [
  'prem@imaginem.mx',
  'madelinne@imaginem.mx'
];

/**
 * Correos con alertas de sonido silenciadas en recepción/sucursal.
 * Exclusivo para:
 * - Prem Gurbani (prem@imaginem.mx)
 * - Madelinne Granados (madelinne@imaginem.mx)
 */
export const AUDIO_ALERT_SILENCED_EMAILS = [
  'prem@imaginem.mx',
  'madelinne@imaginem.mx'
];

/**
 * Comprueba si el usuario tiene PERMISO_SOCIO (Información global/consolidada y financiera reservada)
 * Exclusivo para:
 * - Prem Gurbani (prem@imaginem.mx)
 * - Madelinne Granados (madelinne@imaginem.mx)
 *
 * @param {object} user - Usuario actual de AuthContext
 * @returns {boolean}
 */
export function hasSocioPermission(user) {
  if (!user) return false;
  if (user.is_socio === true) return true;
  if (Array.isArray(user.roles) && (user.roles.includes('socio') || user.roles.includes('PERMISO_SOCIO'))) return true;
  const email = String(user.email || user.username || '').toLowerCase().trim();
  if (SOCIO_EMAILS.includes(email)) return true;
  if (email.startsWith('prem@') || email.startsWith('madelinne@') || email === 'prem' || email === 'madelinne') return true;
  return false;
}

/**
 * Comprueba si el usuario tiene permiso para acceder al panel de Reporte por Médicos y Gratificaciones.
 * Basado en permisos (rol `recepcion.reporte_medicos` asignado al usuario), no exclusivo de socios.
 * Los socios siempre tienen acceso.
 *
 * @param {object} user - Usuario actual de AuthContext
 * @returns {boolean}
 */
export function canAccessReporteMedicos(user) {
  if (!user) return false;
  if (hasSocioPermission(user)) return true;
  if (!Array.isArray(user.roles)) return false;
  const roles = user.roles.map(r => String(r).toLowerCase().trim());
  return roles.includes('recepcion.reporte_medicos') || roles.includes('reporte_medicos');
}

/**
 * Determina si el usuario tiene silenciadas las alertas de sonido de pacientes atrasados/en espera.
 * Exclusivo para:
 * - Prem Gurbani (prem@imaginem.mx)
 * - Madelinne Granados (madelinne@imaginem.mx)
 *
 * @param {object} user - Usuario actual de AuthContext
 * @returns {boolean}
 */
export function isAudioAlertSilencedForUser(user) {
  if (!user) return false;
  const email = String(user.email || user.username || '').toLowerCase().trim();
  return AUDIO_ALERT_SILENCED_EMAILS.some(socioEmail => email === socioEmail || email.includes(socioEmail));
}

/**
 * Determina si el usuario tiene autorización para editar modalidad y nombre de estudios en citas ya admitidas.
 * Exclusivo para:
 * - Eva Aparicio (eva.aparicio@imaginem.mx)
 * - Alexis Moreno (alexis.moreno@imaginem.mx)
 * - Socios y administradores Prem Gurbani y Madelinne Granados (prem@imaginem.mx, madelinne@imaginem.mx)
 *
 * @param {object} user - Usuario actual de AuthContext
 * @returns {boolean}
 */
export function canEditAdmittedStudies(user) {
  if (!user) return false;
  const email = String(user.email || user.username || '').toLowerCase().trim();
  const AUTHORIZED_STUDY_EDIT_EMAILS = [
    'eva.aparicio@imaginem.mx',
    'eva.maria@imaginem.mx',
    'alexis.moreno@imaginem.mx',
    'prem@imaginem.mx',
    'madelinne@imaginem.mx'
  ];
  if (AUTHORIZED_STUDY_EDIT_EMAILS.includes(email)) return true;
  if (hasSocioPermission(user)) return true;
  if (email.startsWith('prem') || email.startsWith('madelinne')) return true;
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  if (roles.includes('admin') && (email.includes('prem') || email.includes('madelinne'))) return true;
  return false;
}


export const MODULOS_CATALOGO = [
  // --- CATEGORÍA 1: ATENCIÓN MÉDICA Y OPERACIONES ---
  {
    categoria: 'Atención Médica y Operaciones',
    modulos: [
      {
        id: 'admin',
        nombre: 'Administrador Total',
        descripcion: 'Acceso completo a todos los módulos y herramientas del sistema.',
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        isSuper: true,
        submodulos: []
      },
      {
        id: 'recepcion',
        nombre: 'Recepción y Flujo de Pacientes',
        descripcion: 'Gestión de citas diarias, admisión, cobro, corte y revisiones.',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        submodulos: [
          { id: 'recepcion.lista', nombre: 'Vista Lista (Citas del Día)', tabId: 'recepcion' },
          { id: 'recepcion.vistacalendario', nombre: 'Vista Calendario Diario', tabId: 'vistacalendario' },
          { id: 'recepcion.kanban', nombre: 'Vista Kanban de Admisión', tabId: 'kanban' },
          { id: 'recepcion.corte', nombre: 'Corte del Día (Finanzas de Caja)', tabId: 'corte' },
          { id: 'recepcion.revision_medicos', nombre: 'Revisión Médicos', tabId: 'revision_medicos' },
          { id: 'recepcion.reporte_medicos', nombre: 'Reporte por Médicos', tabId: 'reporte_medicos' },
          { id: 'recepcion.graficas', nombre: 'Gráficas de Recepción', tabId: 'graficas' },
          { id: 'recepcion.semanal', nombre: 'Vista Semanal', tabId: 'semanal' },
          { id: 'recepcion.mensual', nombre: 'Vista Mensual', tabId: 'mensual' },
          { id: 'recepcion.anual', nombre: 'Vista Anual', tabId: 'anual' },
          { id: 'recepcion.multianual', nombre: 'Vista Multianual', tabId: 'multianual' }
        ]
      },
      {
        id: 'agendamiento',
        nombre: 'Agendamiento y Atención al Paciente',
        descripcion: 'Programación de estudios, agenda, leads y análisis de llamadas.',
        color: 'text-violet-600',
        bg: 'bg-violet-50',
        submodulos: [
          { id: 'agendamiento.manual', nombre: 'Agendamiento Manual', tabId: 'manual' },
          { id: 'agendamiento.calendario', nombre: 'Vista Calendario de Agendamiento', tabId: 'calendario' },
          { id: 'agendamiento.leads', nombre: 'Leads Kommo', tabId: 'leads' },
          { id: 'agendamiento.bi', nombre: 'Análisis de Llamadas (BI)', tabId: 'bi' },
          { id: 'agendamiento.analisis', nombre: 'Análisis de Citas Agendadas', tabId: 'analisis_agendamiento' },
          { id: 'agendamiento.productividad', nombre: 'Productividad ATC (Scorecard y Embudos)', tabId: 'productividad' },
          { id: 'agendamiento.cotizacion', nombre: 'Cotizador de Estudios y Servicios', tabId: 'cotizacion' }
        ]
      },
      {
        id: 'sala_estudios',
        nombre: 'Sala de Estudios Médicos',
        descripcion: 'Monitoreo y asignación en salas de imagenología.',
        color: 'text-teal-600',
        bg: 'bg-teal-50',
        submodulos: [
          { id: 'sala_estudios.todos', nombre: 'Todos los Estudios', tabId: 'sala_todos' },
          { id: 'sala_estudios.us', nombre: 'Ultrasonido (US)', tabId: 'sala_us' },
          { id: 'sala_estudios.rm', nombre: 'Resonancia Magnética (RM)', tabId: 'sala_rm' },
          { id: 'sala_estudios.tac', nombre: 'Tomografía (TAC)', tabId: 'sala_tac' },
          { id: 'sala_estudios.rx', nombre: 'Rayos X (RX)', tabId: 'sala_rx' },
          { id: 'sala_estudios.fl', nombre: 'Fluoroscopía (FL)', tabId: 'sala_fl' }
        ]
      },
      {
        id: 'operaciones',
        nombre: 'Operaciones Médicas',
        descripcion: 'Agenda técnica operativa, verificación de estudios y salas.',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        submodulos: [
          { id: 'operaciones.agenda', nombre: 'Agenda Técnica', tabId: 'agenda' },
          { id: 'operaciones.verificacion', nombre: 'Verificación Técnica', tabId: 'verificacion' },
          { id: 'operaciones.salas', nombre: 'Salas de Estudios', tabId: 'salas' },
          { id: 'operaciones.timeline', nombre: 'Timeline de Salas', tabId: 'timeline' }
        ]
      }
    ]
  },

  // --- CATEGORÍA 2: GESTIÓN EMPRESARIAL Y COMERCIAL ---
  {
    categoria: 'Gestión Empresarial y Comercial',
    modulos: [
      {
        id: 'finance',
        nombre: 'Finanzas Corporativas',
        descripcion: 'Estados de cuenta, ingresos, gastos, flujo de caja correlativo, préstamos y estados financieros.',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        submodulos: [
          { id: 'finance.estados_cuenta', nombre: 'Estados de Cuenta', tabId: 'estados_cuenta' },
          { id: 'finance.ingresos', nombre: 'Ingresos Corporativos', tabId: 'ingresos' },
          { id: 'finance.gastos', nombre: 'Gastos Corporativos', tabId: 'gastos' },
          { id: 'finance.flujo_caja', nombre: 'Flujo de Caja (Libro Maestro)', tabId: 'flujo_caja' },
          { id: 'finance.prestamos', nombre: 'Préstamos a Devolver', tabId: 'prestamos' },
          { id: 'finance.proveedores', nombre: 'Proveedores', tabId: 'proveedores' },
          { id: 'finance.estados_financieros', nombre: 'Estados Financieros', tabId: 'estados_financieros' },
          { id: 'finance.cuentas_pagar', nombre: 'Cuentas por Pagar', tabId: 'cuentas_pagar' },
          { id: 'finance.cuentas_cobrar', nombre: 'Cuentas por Cobrar', tabId: 'cuentas_cobrar' }
        ]
      },
      {
        id: 'hr',
        nombre: 'Recursos Humanos',
        descripcion: 'Gestión de colaboradores, accesos, horarios y checador.',
        color: 'text-emerald-700',
        bg: 'bg-emerald-50',
        submodulos: [
          { id: 'hr.asistencia', nombre: 'Control de Asistencia', tabId: 'rh_asistencia' },
          { id: 'hr.empleados', nombre: 'Gestionar Empleados', tabId: 'rh_empleados' },
          { id: 'hr.checador', nombre: 'Checador Tablet', tabId: 'rh_checador' },
          { id: 'hr.externos', nombre: 'Usuarios Externos', tabId: 'rh_externos' }
        ]
      },
      {
        id: 'crm',
        nombre: 'CRM Médicos y Pacientes',
        descripcion: 'Prospección de médicos referentes, alianzas y pacientes.',
        color: 'text-sky-600',
        bg: 'bg-sky-50',
        submodulos: [
          { id: 'crm.medicos', nombre: 'Pipeline de Médicos', tabId: 'crm_medicos' },
          { id: 'crm.contactos', nombre: 'Directorio Médicos Referentes', tabId: 'crm_directorio' },
          { id: 'crm.pacientes', nombre: 'CRM Pacientes', tabId: 'crm_pacientes' }
        ]
      },
      {
        id: 'marketing',
        nombre: 'Marketing Masivo',
        descripcion: 'Campañas y envíos informativos.',
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        submodulos: []
      },
      {
        id: 'tickets',
        nombre: 'Tickets Internos y Tareas',
        descripcion: 'Gestión corporativa de tareas, peticiones, deadlines y seguimiento.',
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        submodulos: []
      }
    ]
  },

  // --- CATEGORÍA 3: HERRAMIENTAS Y CONFIGURACIÓN ---
  {
    categoria: 'Herramientas y Configuración',
    modulos: [
      {
        id: 'validador',
        nombre: 'Validador de Documentos',
        descripcion: 'Certificación y verificación de códigos QR de recibos y estudios.',
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        submodulos: []
      },
      {
        id: 'tv_turnos',
        nombre: 'Pantalla TV Turnos',
        descripcion: 'Visualización de turnos en sala de espera.',
        color: 'text-cyan-600',
        bg: 'bg-cyan-50',
        submodulos: []
      },
      {
        id: 'dev',
        nombre: 'Herramientas de Desarrollador',
        descripcion: 'Diagnóstico de procesos, importadores batch y simulador.',
        color: 'text-slate-700',
        bg: 'bg-slate-100',
        submodulos: [
          { id: 'dev.tools', nombre: 'Herramientas Dev', tabId: 'dev_tools' },
          { id: 'dev.importador', nombre: 'Importador CSV Batch', tabId: 'dev_import' },
          { id: 'dev.simulador', nombre: 'Simulador Integraciones', tabId: 'dev_sim' },
          { id: 'dev.json', nombre: 'Importador JSON', tabId: 'dev_json' },
          { id: 'dev.processes', nombre: 'System Process Check', tabId: 'dev_sys' }
        ]
      },
      {
        id: 'config_admin',
        nombre: 'Configuración Global',
        descripcion: 'Parámetros del sistema, llaves API e impresoras QZ.',
        color: 'text-slate-800',
        bg: 'bg-slate-100',
        submodulos: []
      }
    ]
  },
  // --- CATEGORÍA: AFILIADOS Y MARKETING EXTERNO ---
  {
    categoria: 'Afiliados y Marketing Externo',
    modulos: [
      {
        id: 'affiliate_marketing',
        nombre: 'Affiliate Marketing Platform',
        descripcion: 'Plataforma exclusiva para afiliados y ejecutivos de marketing externos.',
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        submodulos: [
          { id: 'affiliate.agendamiento', nombre: 'Agendamiento', tabId: 'agendamiento' },
          { id: 'affiliate.facturacion', nombre: 'Facturación', tabId: 'facturacion' },
          { id: 'affiliate.configuracion', nombre: 'Configuración', tabId: 'configuracion' }
        ]
      }
    ]
  },
  // --- CATEGORÍA: RADIOLOGÍA Y ESPECIALISTAS ---
  {
    categoria: 'Radiología y Especialistas',
    modulos: [
      {
        id: 'radiologo',
        nombre: 'Portal Médico Radiólogo',
        descripcion: 'Visualización y gestión clínica de estudios coronarios y agenda de radiología.',
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        submodulos: [
          { id: 'radiologo.agenda', nombre: 'Agenda y Citas Coronarias', tabId: 'agenda' },
          { id: 'radiologo.estudios', nombre: 'Historial y Visor Clínico', tabId: 'estudios' }
        ]
      }
    ]
  }
];

/**
 * Obtiene todos los IDs de submódulos pertenecientes a un módulo padre
 */
export function getSubmodulosIds(moduleId) {
  for (const cat of MODULOS_CATALOGO) {
    for (const mod of cat.modulos) {
      if (mod.id === moduleId) {
        return mod.submodulos.map(s => s.id);
      }
    }
  }
  return [];
}

/**
 * Expande los roles padres heredados para que incluyan automáticamente todos sus submódulos.
 * Garantiza retrocompatibilidad total: si un usuario tiene ['recepcion'], recibe todos los 'recepcion.*'.
 */
export function expandRolesWithDefaults(roles = []) {
  const roleSet = new Set(roles || []);
  
  MODULOS_CATALOGO.forEach(cat => {
    cat.modulos.forEach(mod => {
      if (
        roleSet.has(mod.id) ||
        (mod.id === 'affiliate_marketing' && roleSet.has('affiliate')) ||
        (mod.id === 'radiologo' && roleSet.has('medico_radiologo')) ||
        (mod.id === 'operaciones' && roleSet.has('tecnico'))
      ) {
        const subIds = mod.submodulos.map(s => s.id);
        const hasAnySub = subIds.some(sId => roleSet.has(sId));
        if (!hasAnySub) {
          subIds.forEach(sId => roleSet.add(sId));
        }
      }
    });
  });

  return Array.from(roleSet);
}

/**
 * Comprueba si el usuario tiene acceso a un módulo padre
 */
export function hasModuleAccess(user, moduleId) {
  if (!user) return false;
  if (hasSocioPermission(user)) return true;
  if (moduleId === 'recepcion' && canAccessReporteMedicos(user)) return true;
  if (!Array.isArray(user.roles)) return false;
  const roles = user.roles;
  if (roles.includes('admin')) return true;
  if (moduleId === 'affiliate_marketing' && (roles.includes('affiliate') || roles.includes('affiliate.agendamiento') || roles.includes('affiliate.facturacion') || roles.includes('affiliate.configuracion') || roles.includes('marketing'))) return true;
  if (moduleId === 'radiologo' && (roles.includes('radiologo') || roles.includes('radiologo.agenda') || roles.includes('radiologo.estudios') || roles.includes('medico'))) return true;
  if (moduleId === 'operaciones' && (roles.includes('operaciones') || roles.includes('tecnico'))) return true;
  if (roles.includes(moduleId)) return true;
  // También si tiene cualquiera de sus submódulos
  const subPrefix = moduleId + '.';
  return roles.some(r => typeof r === 'string' && r.startsWith(subPrefix));
}

/**
 * Comprueba si el usuario tiene acceso a un submódulo específico
 * @param {object} user - Usuario actual de AuthContext
 * @param {string} moduleId - ID del módulo padre (ej. 'recepcion')
 * @param {string} subId - ID del submódulo (ej. 'recepcion.lista', 'recepcion.corte' o tabId 'recepcion', 'corte')
 */
export function hasSubmoduleAccess(user, moduleId, subId) {
  if (!user) return false;
  if (hasSocioPermission(user)) return true;
  if (moduleId === 'recepcion' && (subId === 'reporte_medicos' || subId === 'recepcion.reporte_medicos') && canAccessReporteMedicos(user)) return true;
  if (!Array.isArray(user.roles)) return false;
  const roles = user.roles;
  if (roles.includes('admin')) return true;

  if (!subId) return hasModuleAccess(user, moduleId);

  // Buscar definición del submódulo en el catálogo
  let targetSub = null;
  for (const cat of MODULOS_CATALOGO) {
    for (const mod of cat.modulos) {
      if (mod.id === moduleId) {
        targetSub = mod.submodulos.find(s => 
          s.id === subId || 
          s.tabId === subId || 
          s.id === `${moduleId}.${subId}` ||
          s.id.endsWith(`.${subId}`)
        );
        break;
      }
    }
    if (targetSub) break;
  }

  // Recolectar todas las formas posibles del ID del permiso
  const candidateKeys = new Set();
  candidateKeys.add(subId);
  if (moduleId && !subId.includes('.')) {
    candidateKeys.add(`${moduleId}.${subId}`);
  }
  if (targetSub) {
    candidateKeys.add(targetSub.id);
    if (targetSub.tabId) {
      candidateKeys.add(targetSub.tabId);
      if (moduleId) candidateKeys.add(`${moduleId}.${targetSub.tabId}`);
    }
  }

  // Mapeos y alias específicos de tabs vs permisos
  if (moduleId === 'recepcion' && (subId === 'recepcion' || subId === 'lista' || subId === 'recepcion.lista' || subId === 'recepcion.recepcion')) {
    candidateKeys.add('recepcion.lista');
    candidateKeys.add('recepcion.recepcion');
    candidateKeys.add('recepcion');
  }
  if (moduleId === 'agendamiento' && (subId === 'analisis' || subId === 'analisis_agendamiento' || subId === 'agendamiento.analisis' || subId === 'agendamiento.analisis_agendamiento')) {
    candidateKeys.add('agendamiento.analisis');
    candidateKeys.add('agendamiento.analisis_agendamiento');
  }
  if (moduleId === 'hr' && (subId === 'general' || subId === 'rh_general' || subId === 'hr.general' || subId === 'hr.rh_general')) {
    candidateKeys.add('hr.general');
    candidateKeys.add('hr.rh_general');
  }
  if (moduleId === 'hr' && (subId === 'empleados' || subId === 'rh_empleados' || subId === 'hr.empleados' || subId === 'hr.rh_empleados')) {
    candidateKeys.add('hr.empleados');
    candidateKeys.add('hr.rh_empleados');
  }
  if (moduleId === 'hr' && (subId === 'checador' || subId === 'rh_checador' || subId === 'hr.checador' || subId === 'hr.rh_checador')) {
    candidateKeys.add('hr.checador');
    candidateKeys.add('hr.rh_checador');
  }
  if (moduleId === 'crm' && (subId === 'contactos' || subId === 'crm_directorio' || subId === 'crm.contactos' || subId === 'crm.crm_directorio')) {
    candidateKeys.add('crm.contactos');
    candidateKeys.add('crm.crm_directorio');
  }
  if (moduleId === 'affiliate_marketing' && (subId === 'agendamiento' || subId === 'affiliate_agendamiento' || subId === 'affiliate.agendamiento')) {
    candidateKeys.add('affiliate.agendamiento');
    candidateKeys.add('affiliate_agendamiento');
    candidateKeys.add('agendamiento');
    candidateKeys.add('affiliate');
  }
  if (moduleId === 'affiliate_marketing' && (subId === 'facturacion' || subId === 'affiliate_facturacion' || subId === 'affiliate.facturacion')) {
    candidateKeys.add('affiliate.facturacion');
    candidateKeys.add('affiliate_facturacion');
    candidateKeys.add('facturacion');
    candidateKeys.add('affiliate');
    candidateKeys.add('affiliate.agendamiento');
  }
  if (moduleId === 'affiliate_marketing' && (subId === 'configuracion' || subId === 'affiliate_configuracion' || subId === 'affiliate.configuracion')) {
    candidateKeys.add('affiliate.configuracion');
    candidateKeys.add('affiliate_configuracion');
    candidateKeys.add('configuracion');
    candidateKeys.add('affiliate');
    candidateKeys.add('affiliate.agendamiento');
  }
  if (moduleId === 'radiologo' && (subId === 'agenda' || subId === 'radiologo.agenda')) {
    candidateKeys.add('radiologo.agenda');
    candidateKeys.add('agenda');
  }
  if (moduleId === 'radiologo' && (subId === 'estudios' || subId === 'radiologo.estudios')) {
    candidateKeys.add('radiologo.estudios');
    candidateKeys.add('estudios');
  }
  if (moduleId === 'operaciones' && (subId === 'verificacion' || subId === 'operaciones.verificacion')) {
    candidateKeys.add('operaciones.verificacion');
    candidateKeys.add('verificacion');
  }

  // 1. Si el usuario tiene asignada cualquiera de las formas del permiso
  for (const key of candidateKeys) {
    if (roles.includes(key)) return true;
  }

  // 2. Si tiene el rol padre del módulo y no tiene submódulos granulares restringidos:
  if (
    roles.includes(moduleId) ||
    (moduleId === 'operaciones' && roles.includes('tecnico')) ||
    (moduleId === 'affiliate_marketing' && (roles.includes('affiliate') || roles.includes('marketing'))) ||
    (moduleId === 'radiologo' && (roles.includes('radiologo') || roles.includes('medico')))
  ) {
    const allSubs = getSubmodulosIds(moduleId);
    const hasConfiguredSubs = allSubs.some(s => roles.includes(s));
    if (!hasConfiguredSubs) {
      return true;
    }
  }

  return false;
}
